'use client';

import * as React from 'react';
import { DayPicker, getDefaultClassNames } from '@daypicker/react';
import { nl } from '@daypicker/react/locale';

import { cn } from '@utils';

export function Calendar({
  className,
  classNames,
  locale = nl,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      className={cn('w-full font-sans text-primary', defaultClassNames.root, className)}
      classNames={{
        root: cn(defaultClassNames.root, 'w-full'),
        months: cn(defaultClassNames.months, 'relative flex flex-col'),
        month: cn(defaultClassNames.month, 'flex w-full flex-col gap-3'),
        month_caption: cn(
          defaultClassNames.month_caption,
          'flex h-11 items-center justify-center px-12',
        ),
        caption_label: cn(
          defaultClassNames.caption_label,
          'font-heading text-xl font-semibold capitalize text-accent',
        ),
        dropdowns: cn(defaultClassNames.dropdowns, 'flex items-center justify-center gap-1'),
        dropdown_root: cn(
          defaultClassNames.dropdown_root,
          'rounded-md border border-primary/15 bg-muted/70 px-2 py-1 text-accent transition-colors hover:border-primary/30',
        ),
        dropdown: cn(
          defaultClassNames.dropdown,
          'cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40',
        ),
        nav: cn(
          defaultClassNames.nav,
          'absolute inset-x-0 top-0 flex h-11 items-center justify-between',
        ),
        button_previous: cn(
          defaultClassNames.button_previous,
          'inline-flex size-11 items-center justify-center rounded-full text-primary transition-colors hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-30',
        ),
        button_next: cn(
          defaultClassNames.button_next,
          'inline-flex size-11 items-center justify-center rounded-full text-primary transition-colors hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-30',
        ),
        chevron: cn(defaultClassNames.chevron, 'size-4 fill-current'),
        month_grid: cn(defaultClassNames.month_grid, 'w-full border-collapse'),
        weekdays: cn(defaultClassNames.weekdays, 'border-b border-primary/10'),
        weekday: cn(
          defaultClassNames.weekday,
          'h-10 p-0 text-center text-xs font-semibold uppercase tracking-[0.12em] text-primary/55',
        ),
        week: cn(defaultClassNames.week, 'border-0'),
        day: cn(defaultClassNames.day, 'size-11 p-0 text-center'),
        day_button: cn(
          defaultClassNames.day_button,
          'inline-flex size-11 items-center justify-center rounded-full border border-transparent text-sm font-medium text-primary transition-colors hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40',
        ),
        selected: cn(
          defaultClassNames.selected,
          '[&>button]:border-primary [&>button]:bg-primary [&>button]:text-light [&>button]:shadow-soft [&>button]:hover:bg-primary/90',
        ),
        today: cn(
          defaultClassNames.today,
          '[&>button]:border-secondary [&>button]:font-bold [&>button]:text-accent',
        ),
        outside: cn(defaultClassNames.outside, '[&>button]:text-primary/30'),
        disabled: cn(
          defaultClassNames.disabled,
          '[&>button]:cursor-not-allowed [&>button]:text-primary/20 [&>button]:hover:bg-transparent',
        ),
        hidden: cn(defaultClassNames.hidden, 'invisible'),
        ...classNames,
      }}
      locale={locale}
      showOutsideDays={showOutsideDays}
      {...props}
    />
  );
}
